/* v3.3 migration · pack resultats-clients — the report model behind Résultats (WF-023 · WF-100 … WF-106 · WF-007).
   ONE model: the scope read from the URL, the sections each view draws, and the CSV written from those very sections
   (« CSV = the view »: the same rows the screen shows, never a second query). Every figure comes from the data the app
   already has — the participation ledger, the supports' own numbers, the members' till ledger — or is said « pas encore
   mesuré ». An unknown cost is coverage (known count + missing count), never a guessed total. Scoped reporting against
   the real API is Technical's T4.1: the scope is applied client-side until then. */
import type { LedgerRow } from '@/mocks/data-b'
import { platformLabel, supportNumbers, type PlatformId, type SupportRef } from '@/lib/campaign'

export type Vue = 'ensemble' | 'cadeaux' | 'demandes' | 'fidelite' | 'supports' | 'equipe' | 'definitions'
export const VUES: [Vue, string][] = [['ensemble', 'Vue d’ensemble'], ['cadeaux', 'Cadeaux'], ['demandes', 'Demandes'], ['fidelite', 'Fidélité'], ['supports', 'Supports'], ['equipe', 'Équipe'], ['definitions', 'Définitions']]
/** the paths the app already links to (Accueil, Cadeaux, the campaign page) keep landing on the right tab */
const ALIASES: Record<string, Vue> = { tableau: 'ensemble', reponses: 'demandes', quiz: 'demandes', export: 'ensemble', google: 'ensemble' }
export const vueOf = (v: string | null): Vue => (VUES.some(([id]) => id === v) ? (v as Vue) : (v && ALIASES[v]) || 'ensemble')

export type Periode = 7 | 30
export const PERIODES: [Periode, string][] = [[7, '7 derniers jours'], [30, '30 derniers jours']]
export interface Scope { periode: Periode; campagne: string | null; support: string | null; emplacement: string | null; demande: string | null }
export const DEFAULT_SCOPE: Scope = { periode: 30, campagne: null, support: null, emplacement: null, demande: null }
export function readScope(p: URLSearchParams): Scope {
  return {
    periode: p.get('periode') === '7' ? 7 : 30,
    campagne: p.get('campagne') || null, support: p.get('support') || null,
    emplacement: p.get('emplacement') || null, demande: p.get('demande') || null,
  }
}
/** The scope written back: only what differs from the default, so a plain /resultats stays plain. */
export function writeScope(p: URLSearchParams, s: Scope): URLSearchParams {
  const q = new URLSearchParams(p)
  const set = (k: string, v: string | null) => { if (v) q.set(k, v); else q.delete(k) }
  set('periode', s.periode === 30 ? null : String(s.periode))
  set('campagne', s.campagne); set('support', s.support); set('emplacement', s.emplacement); set('demande', s.demande)
  return q
}
export const isFiltered = (s: Scope) => s.periode !== 30 || !!s.campagne || !!s.support || !!s.emplacement || !!s.demande
export const periodeLabel = (p: Periode) => PERIODES.find(([n]) => n === p)![1]

export const NOT_MEASURED = 'pas encore mesuré'
export const EMPTY_SCOPE = 'Aucun résultat dans ce périmètre. Modifiez la période ou effacez les filtres.'
/** The vocabulary: the app counts « Scans » (Scope v5 §2); the reference says « Ouvertures » ⟦confirmable⟧ — kept in the definition. */
export const SCANS = 'Scans'

export type Cell = string | number
export interface Section { id: string; title: string; columns: string[]; rows: Cell[][]; note?: string; empty?: string; definition?: string }

/* ---------- the inputs every view reads ---------- */
export interface ReportInputs {
  scope: Scope
  now: number
  /** every participation the app knows (unscoped) */
  ledger: LedgerRow[]
  supports: SupportRef[]
  /** the supports of the current establishment context (D-31), or all */
  locationId: string | null
  campaignNames: Record<string, string>
  asks: { id: string; kind: string; name: string; questions?: { id: string; text: string; choices: string[]; correct: number }[]; question?: string; choices?: string[] }[]
  /** private feedback count per ask (lib/messages) — never read from the ledger */
  messagesOf: (askId: string) => number
  members: { id: string; firstName: string; locationId: string | null }[]
  /** the till's PIN ledger (30 days) — null while it loads or with an API */
  validated: Record<string, number> | null
}
/** The supports the scope lists: the context's, one support, or one place. */
export function supportsInScope(i: ReportInputs): SupportRef[] {
  return i.supports.filter(s => (!i.locationId || s.locationId === i.locationId) && (!i.scope.support || s.id === i.scope.support) && (!i.scope.emplacement || (s.place?.trim() || '') === i.scope.emplacement))
}
const periodStart = (i: ReportInputs) => i.now - i.scope.periode * 86400e3
const inPeriod = (i: ReportInputs, iso: string) => { const t = Date.parse(iso); return t >= periodStart(i) && t <= i.now }
/** The participations of the scope: period, campaign, support / place / establishment, demande. */
export function rowsInScope(i: ReportInputs): LedgerRow[] {
  const sids = new Set(supportsInScope(i).map(s => s.id))
  const narrowSupports = !!(i.locationId || i.scope.support || i.scope.emplacement)
  return i.ledger.filter(r => inPeriod(i, r.at)
    && (!i.scope.campagne || r.campaignId === i.scope.campagne)
    && (!narrowSupports || (r.supportId !== null && sids.has(r.supportId)))
    && (!i.scope.demande || r.ask?.askId === i.scope.demande))
}
/** Gifts USED in the period, whatever period they were won in (WF-100's caveat), in the same campaign / support scope. */
export function usedInScope(i: ReportInputs): LedgerRow[] {
  const sids = new Set(supportsInScope(i).map(s => s.id))
  const narrowSupports = !!(i.locationId || i.scope.support || i.scope.emplacement)
  return i.ledger.filter(r => r.gift?.usedAt && inPeriod(i, r.gift.usedAt)
    && (!i.scope.campagne || r.campaignId === i.scope.campagne)
    && (!narrowSupports || (r.supportId !== null && sids.has(r.supportId)))
    && (!i.scope.demande || r.ask?.askId === i.scope.demande))
}
/** The scans the app has per support: 7 days as counted, 30 days as the support's own numbers say. Not per campaign nor per demande. */
export function scansOf(i: ReportInputs, s: SupportRef): number | null {
  if (i.scope.campagne || i.scope.demande) return null
  return i.scope.periode === 7 ? s.scans7d : supportNumbers(s).scans30
}
const sum = (xs: (number | null)[]): number | null => xs.some(x => x === null) ? null : xs.reduce((a, b) => (a ?? 0) + (b ?? 0), 0)
const cell = (n: number | null): Cell => (n === null ? NOT_MEASURED : n)
const eur = (cents: number) => `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

/* ---------- the sections, one builder per view ---------- */
export const DEFINITIONS: [string, string][] = [
  [`${SCANS} (ouvertures du parcours)`, 'Ouvertures du lien BMR, pas nécessairement des scans physiques.'],
  ['Participations', 'Parcours éligibles démarrés ; ni rendez-vous ni personnes uniques.'],
  ['Cadeaux gagnés', 'Cadeaux attribués indépendamment des demandes.'],
  ['Cadeaux utilisés', 'Validations de la période, moins les annulations.'],
  ['Liens de demande ouverts', 'Ouvertures observées, jamais avis publiés ou abonnés confirmés.'],
  ['Coût', 'Coûts privés des cadeaux utilisés ; estimation partielle si des coûts manquent.'],
  ['Attribution', 'Personne et support enregistrés au moment de la participation.'],
  ['Période', 'Heure du salon : Europe/Paris. Tests exclus.'],
]
const def = (name: string) => DEFINITIONS.find(([n]) => n.startsWith(name))?.[1]

export function ensembleSections(i: ReportInputs): Section[] {
  const rows = rowsInScope(i)
  const scans = i.ledger.length ? sum(supportsInScope(i).map(s => scansOf(i, s))) : null
  return [{
    id: 'ensemble', title: 'Vue d’ensemble', columns: ['Indicateur', 'Nombre'],
    rows: [
      [`${SCANS} (ouvertures du parcours)`, cell(scans)],
      ['Participations', i.ledger.length ? rows.length : NOT_MEASURED],
      ['Cadeaux gagnés', i.ledger.length ? rows.filter(r => r.gift).length : NOT_MEASURED],
      ['Cadeaux utilisés', i.ledger.length ? usedInScope(i).length : NOT_MEASURED],
      ['Liens de demande ouverts', i.ledger.length ? rows.filter(r => r.invitation?.opened).length : NOT_MEASURED],
    ],
    note: 'Aucune publication d’avis ni croissance d’abonnés n’est déduite des ouvertures.',
    definition: def('Participations'),
  }]
}

export function cadeauxSections(i: ReportInputs): Section[] {
  const won = rowsInScope(i).filter(r => r.gift)
  const used = usedInScope(i)
  const types = new Map<string, { name: string; won: number; used: number }>()
  for (const r of won) { const t = types.get(r.gift!.typeId) ?? { name: r.gift!.typeName, won: 0, used: 0 }; t.won++; types.set(r.gift!.typeId, t) }
  for (const r of used) { const t = types.get(r.gift!.typeId) ?? { name: r.gift!.typeName, won: 0, used: 0 }; t.used++; types.set(r.gift!.typeId, t) }
  const known = used.filter(r => r.gift!.costCents !== null)
  return [
    { id: 'cadeaux', title: 'Cadeaux sur la période', columns: ['Cadeau', 'Gagnés', 'Utilisés'],
      rows: [...types.values()].sort((a, b) => b.won - a.won || b.used - a.used).map(t => [t.name, t.won, t.used]),
      empty: i.ledger.length ? EMPTY_SCOPE : `Cadeaux gagnés et utilisés par période : ${NOT_MEASURED}.`,
      note: 'Les utilisations peuvent concerner des cadeaux gagnés avant cette période. Ce tableau ne constitue pas un taux de conversion de cohorte.',
      definition: def('Cadeaux gagnés') },
    { id: 'cout', title: 'Coût des cadeaux utilisés', columns: ['Mesure', 'Valeur'],
      rows: i.ledger.length ? [
        ['Coût connu (€)', eur(known.reduce((a, r) => a + (r.gift!.costCents ?? 0), 0))],
        ['Coûts connus', known.length],
        ['Coûts manquants', used.length - known.length],
      ] : [['Coût connu (€)', NOT_MEASURED], ['Coûts connus', NOT_MEASURED], ['Coûts manquants', NOT_MEASURED]],
      note: 'Estimation privée du salon, pas un frais BMR.', definition: def('Coût') },
  ]
}

export function demandesSections(i: ReportInputs): Section[] {
  const rows = rowsInScope(i)
  const plats = new Map<string, { shown: number; opened: number }>()
  for (const r of rows) if (r.invitation) { const p = plats.get(r.invitation.platform) ?? { shown: 0, opened: 0 }; p.shown++; if (r.invitation.opened) p.opened++; plats.set(r.invitation.platform, p) }
  const out: Section[] = [{
    id: 'invitations', title: 'Invitations vers vos plateformes', columns: ['Plateforme', 'Invitations affichées', 'Liens ouverts'],
    rows: [...plats.entries()].map(([p, v]) => [platformLabel(p as PlatformId), v.shown, v.opened]),
    empty: i.ledger.length ? EMPTY_SCOPE : `Invitations affichées et liens ouverts par plateforme : ${NOT_MEASURED}.`,
    note: 'Un lien ouvert ou un retour d’onglet ne prouve pas un avis publié ni un abonnement. Les événements inconnus ne sont pas réattribués.',
    definition: def('Liens de demande ouverts'),
  }]
  /* one table per question: every answer, and the « sortie » row (every ask is skippable) */
  for (const a of i.asks) {
    if (i.scope.demande && a.id !== i.scope.demande) continue
    const answered = rows.filter(r => r.ask?.askId === a.id)
    if (!answered.length) continue
    const questions = a.kind === 'quiz' ? (a.questions ?? []).map(q => ({ key: q.id, text: q.text, choices: q.choices })) : [{ key: 'q', text: a.question || a.name, choices: a.choices ?? [] }]
    for (const q of questions) {
      const counts = new Map<string, number>()
      for (const r of answered) {
        const v = r.ask!.answers[q.key]; if (v === undefined) continue
        const label = v === 'sortie' ? 'sortie' : v === 'libre' ? 'réponse libre' : q.choices[v] ?? `réponse ${v + 1}`
        counts.set(label, (counts.get(label) ?? 0) + 1)
      }
      out.push({ id: `q-${a.id}-${q.key}`, title: `${q.text} · ${a.name}`, columns: ['Réponse / sortie', 'Nombre'],
        rows: [...q.choices.map(c => [c, counts.get(c) ?? 0] as Cell[]), ...(counts.has('réponse libre') ? [['réponse libre', counts.get('réponse libre')!] as Cell[]] : []), ['sortie', counts.get('sortie') ?? 0]],
        note: 'Réponses facultatives : la ligne « sortie » compte les passages sans réponse.' })
    }
  }
  return out
}

export function fideliteSections(i: ReportInputs): Section[] {
  const rows = rowsInScope(i)
  const start = periodStart(i)
  const byClient = new Map<string, { name: string; n: number }>()
  for (const r of rows) if (r.client) { const c = byClient.get(r.client.id) ?? { name: r.client.name, n: 0 }; c.n++; byClient.set(r.client.id, c) }
  const earlier = (id: string) => i.ledger.some(r => r.client?.id === id && Date.parse(r.at) < start)
  return [{
    id: 'fidelite', title: 'Clients revenus sur BMR', columns: ['Client identifié', 'Participations dans la période', 'Participation BMR antérieure'],
    rows: [...byClient.entries()].filter(([id]) => earlier(id)).sort((a, b) => b[1].n - a[1].n).map(([, c]) => [c.name, c.n, 'Oui']),
    empty: i.ledger.length ? EMPTY_SCOPE : `Clients revenus par période : ${NOT_MEASURED}.`,
    note: 'Participants identifiés du périmètre ; antériorité recherchée dans l’historique BMR du salon. Les participations anonymes sont exclues. Ce ne sont pas des rendez-vous au salon.',
    definition: def('Participations'),
  }]
}

export function supportsSections(i: ReportInputs): Section[] {
  const rows = rowsInScope(i); const used = usedInScope(i)
  const sups = supportsInScope(i)
  return [{
    id: 'supports', title: 'Activité par support', columns: ['Support', SCANS, 'Participations', 'Cadeaux gagnés', 'Cadeaux utilisés', 'Liens ouverts'],
    rows: sups.map(s => {
      const mine = rows.filter(r => r.supportId === s.id)
      return [s.name, cell(i.ledger.length ? scansOf(i, s) : null), i.ledger.length ? mine.length : NOT_MEASURED, i.ledger.length ? mine.filter(r => r.gift).length : NOT_MEASURED,
        i.ledger.length ? used.filter(r => r.supportId === s.id).length : NOT_MEASURED, i.ledger.length ? mine.filter(r => r.invitation?.opened).length : NOT_MEASURED]
    }),
    empty: EMPTY_SCOPE,
    note: 'Les ouvertures ne sont pas nécessairement des scans physiques.',
    definition: def(SCANS),
  }]
}

export function equipeSections(i: ReportInputs): Section[] {
  const rows = rowsInScope(i)
  const members = i.members.filter(m => !i.locationId || m.locationId === null || m.locationId === i.locationId)
  const thirty = i.scope.periode === 30 && !i.scope.campagne && !i.scope.support && !i.scope.emplacement && !i.scope.demande
  const validatedOf = (id: string): number | null => (thirty && i.validated ? (i.validated[id] ?? 0) : null)
  const attributed = (id: string) => rows.filter(r => r.memberId === id).length
  const unattributed = rows.filter(r => !r.memberId).length
  const sumValidated = members.reduce((a, m) => a + (validatedOf(m.id) ?? 0), 0)
  return [{
    id: 'equipe', title: 'Participations attribuées à l’équipe', columns: ['Membre', 'Participations', 'Cadeaux validés en caisse'],
    rows: [
      ...members.map(m => [m.firstName, i.ledger.length ? attributed(m.id) : NOT_MEASURED, cell(validatedOf(m.id))] as Cell[]),
      ['Non attribué', i.ledger.length ? unattributed : NOT_MEASURED, thirty && i.validated && i.ledger.length ? Math.max(0, usedInScope(i).length - sumValidated) : NOT_MEASURED],
    ],
    note: 'Attribution au moment de la participation. Un changement de support ne modifie pas l’historique. Les validations caisse ne sont pas des participations acquises.',
    definition: def('Attribution'),
  }]
}

export const definitionsSections = (): Section[] => [{ id: 'definitions', title: 'Définitions', columns: ['Mesure', 'Ce qui est compté'], rows: DEFINITIONS.map(([a, b]) => [a, b]) }]

export function sectionsOf(vue: Vue, i: ReportInputs): Section[] {
  switch (vue) {
    case 'cadeaux': return cadeauxSections(i)
    case 'demandes': return demandesSections(i)
    case 'fidelite': return fideliteSections(i)
    case 'supports': return supportsSections(i)
    case 'equipe': return equipeSections(i)
    case 'definitions': return definitionsSections()
    default: return ensembleSections(i)
  }
}

/* ---------- « Exporter cette vue » — the CSV is these sections, nothing else ---------- */
const csvCell = (v: Cell) => { const s = String(v); return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
export function csvOf(sections: Section[], meta: { perimetre: string; vue: string }): string {
  const lines: string[] = [
    ['Périmètre', meta.perimetre].map(csvCell).join(';'), ['Vue', meta.vue].map(csvCell).join(';'),
    ['Fuseau', 'Europe/Paris'].map(csvCell).join(';'), ['Tests', 'Exclus'].map(csvCell).join(';'), '',
  ]
  for (const s of sections) {
    lines.push(csvCell(s.title), s.columns.map(csvCell).join(';'))
    if (s.rows.length) for (const r of s.rows) lines.push(r.map(csvCell).join(';'))
    else lines.push(csvCell(s.empty ?? EMPTY_SCOPE))
    if (s.definition) lines.push(['Définition', s.definition].map(csvCell).join(';'))
    lines.push('')
  }
  return `﻿${lines.join('\r\n')}`
}
const slug = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'toutes'
/** BMR-<vue>-<campagne>.csv ⟦confirmable⟧ (the prototype's name) */
export const csvFileName = (vue: Vue, campagne: string | null) => `BMR-${vue}-${slug(campagne ?? 'toutes-les-campagnes')}.csv`
export function downloadCsv(name: string, text: string): void {
  try {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  } catch { /* a browser without downloads: nothing to do */ }
}
